import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { GuideFieldGrid, GuideHelpText } from '../../../theme/components/GuideField';
import { ImportAssignment } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ImportAssignmentFields } from './ImportAssignmentFields';
import { AssignmentOptions } from './useAssignmentOptions';

export interface ImportAssignmentDialogProps {
  open: boolean;
  /** Fila del archivo (título del diálogo). */
  rowNumber?: number;
  /** Texto del ítem (contexto; se muestra como texto). */
  itemName?: string | null;
  /** Asignación con que abre (la efectiva de la fila, si la hay). */
  initial?: ImportAssignment;
  options: AssignmentOptions;
  onDismiss: () => void;
  /** Guarda la asignación propia de la fila. */
  onSave: (assignment: ImportAssignment) => void;
}

/** "Cambiar protocolo o paciente" de una fila (spec 009, US2): asignación propia que reemplaza a la general. */
export const ImportAssignmentDialog = ({ open, rowNumber, itemName, initial, options, onDismiss, onSave }: ImportAssignmentDialogProps) => {
  const { t } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => t(`providerConsumptionImport.review.assignment.${key}`, params) as string;
  const [value, setValue] = useState<ImportAssignment | undefined>(initial);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue(initial);
    setSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = () => {
    setSubmitted(true);
    if (!value?.protocolId) return;
    onSave(value);
  };

  return (
    <Dialog open={open} onOpenChange={(_event, data) => !data.open && onDismiss()}>
      <DialogSurface data-testid="pci-assignment-dialog">
        <DialogBody>
          <DialogTitle>{tr('dialogTitle', { row: rowNumber ?? '' })}</DialogTitle>
          <DialogContent>
            <div className="pci-stack">
              {itemName && <GuideHelpText className="pci-review-name">{itemName}</GuideHelpText>}
              <GuideFieldGrid>
                <ImportAssignmentFields
                  options={options}
                  value={value}
                  onChange={setValue}
                  protocolError={submitted && !value?.protocolId ? tr('protocolRequired') : undefined}
                  testIdPrefix="pci-assignment-dialog"
                />
              </GuideFieldGrid>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onDismiss} data-testid="pci-assignment-dialog-cancel">
              {tr('cancel')}
            </Button>
            <Button appearance="primary" onClick={save} data-testid="pci-assignment-dialog-save">
              {tr('save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ImportAssignmentDialog;
